import { Injectable } from '@angular/core';

import { Categories } from '../casino-lobby-manager/lobby-details-model/categories.model';
import { CategoryFeatureMetadata } from '../casino-lobby-manager/lobby-details-model/category-feature-metadata-model';
import { LobbyItemBaseModel } from '../casino-lobby-manager/lobby-details-model/lobby-item.model';
import {
    CategoryTable,
    FIXEDPREFIX_TO_KEY,
    GameMetadataTable,
    LobbyTable,
    StickerDetailsTable,
    SubCategoryTable,
    TABLES,
    categoryNameLangTable,
} from './casino-index-db.model';
import { CasinoIndexDBService } from './casino-index-db.service';
import { LmtRecordToModelReaderService } from './lmt-record-to-model-reader.service';

@Injectable({
    providedIn: 'root',
})
export class CasinoLmtResponseToTables {
    constructor(
        private casinoIndexDBService: CasinoIndexDBService,
        private lmtRecordToModelReaderService: LmtRecordToModelReaderService,
    ) {}

    async addLobbyDataToIndexDbTables(
        lobbyType: string,
        invokerProduct: string,
        lobbyData: LobbyItemBaseModel,
        cacheKey: string,
        collectionKey: string,
    ) {
        const uniqueKey = `${collectionKey}-${lobbyType}`;
        if (this.casinoIndexDBService.hasLobbyBeenAdded(uniqueKey)) {
            return;
        }
        this.casinoIndexDBService.markLobbyAsAdded(uniqueKey);

        const dataIdentifierKey = cacheKey.replace(FIXEDPREFIX_TO_KEY, '');
        const lobbyDetails = await this.lmtRecordToModelReaderService.getLobby(lobbyType, dataIdentifierKey);
        if (lobbyDetails != null) {
            return;
        }
        await this.addLobbies(
            lobbyType,
            invokerProduct,
            lobbyData?.lbyTempName,
            lobbyData.lobbyGameIconImageSuffix,
            lobbyData?.optSegName,
            dataIdentifierKey,
            collectionKey,
            lobbyData?.lbyTargetType,
        );
        await this.addCategoriesRecordsFromCategoriesMap(lobbyData?.mainCategoriesToStore, lobbyType, dataIdentifierKey, collectionKey);
        await this.addSubcategories(lobbyData.lsCategoriesMap, lobbyType, dataIdentifierKey, collectionKey);
        await this.addGameMetaData(lobbyData.lsGameMetaDataMap, lobbyType, dataIdentifierKey, collectionKey);
        await this.addStickerDetails(lobbyData.lsStickerNameMap, lobbyType, dataIdentifierKey, collectionKey);
        await this.addcategoryVsFeatureMetadata(lobbyData.catVsFeatureMetadata, lobbyType, dataIdentifierKey, collectionKey);
        await this.addCategoryNameLangMap(lobbyData.lsCategorytNameLangMap, lobbyType, dataIdentifierKey, collectionKey);
        await this.casinoIndexDBService.clearExpiredDataSet();
    }

    async addCategoriesRecordsFromCategoriesMap(
        categoriesMap: [string, Categories][] | undefined,
        lobbyType: string,
        dataIdentifierKey: string,
        collectionKey: string,
    ) {
        if (!categoriesMap) {
            return;
        }
        let categoryRecords: CategoryTable[] = [];
        for (let i = 0; i < categoriesMap.length; i++) {
            const category = categoriesMap[i][1];
            categoryRecords.push({
                categoryId: category.categoryid,
                lobbyType: lobbyType,
                route: category.route,
                categoryName: category.categoryname,
                games: category.gamelist ? category.gamelist.map((game) => ({ game: game.game, icon: game.icon, sticker: game.sticker })) : [],
                sid: category.sid,
                rowcount: category.rowcount,
                lmtCategoryId: category.lmtCategoryId,
                subCategoryVisible: category.isSubCatVisible,
                engCategoryName: category.engCategoryName,
                navigationCategory: category.isNavigationCategory,
                lmtSubCategories: category.lmtSubCategories,
                sitecoreData: category.sitecoreData || [],
                iconpositions: category.iconpositions,
                dataIdentifierKey: dataIdentifierKey,
                collectionKey: collectionKey,
            });
        }

        await this.casinoIndexDBService.addRecords<CategoryTable>(TABLES.CATEGORY, categoryRecords);
    }

    async addSubcategories(categoriesMap: [string, Categories][], lobbyType: string, dataIdentifierKey: string, collectionKey: string) {
        let subCategoryRecords: SubCategoryTable[] = [];
        for (let i = 0; i < categoriesMap.length; i++) {
            const category = categoriesMap[i][1];
            category?.subcategories?.forEach((subcategory) => {
                subCategoryRecords.push({
                    subcategoryId: subcategory.subcategoryid,
                    route: subcategory.route,
                    subCategoryName: subcategory.subcategoryname,
                    games: subcategory.gamelist
                        ? subcategory.gamelist.map((game) => ({ game: game.game, icon: game.icon, sticker: game.sticker }))
                        : [],
                    lmtCategoryId: subcategory.lmtCategoryId,
                    categoryId: subcategory.categoryid,
                    isSubCatVisible: subcategory.isSubCatVisible,
                    engCategoryName: subcategory.engCategoryName,
                    categoryName: subcategory.categoryname,
                    sitecoreData: subcategory.sitecoreData,
                    hideSubCategory: subcategory.hideSubCategory,
                    subTitle: subcategory.subTitle,
                    parentCategoryId: subcategory.parentCategoryId,
                    parentLMTCategoryId: subcategory.parentLMTCategoryId,
                    filterRecentlyPlayedGames: subcategory.filterRecentlyPlayedGames,
                    havingCasiaGames: subcategory.havingCasiaGames,
                    filterFeatureIds: subcategory.filterFeatureIds,
                    lobbyType: lobbyType,
                    linkedCategory: subcategory.linkedCategory,
                    rowCount: subcategory.rowCount,
                    dataIdentifierKey: dataIdentifierKey,
                    collectionKey: collectionKey,
                });
            });
        }
        await this.casinoIndexDBService.addRecords<SubCategoryTable>(TABLES.SUB_CATEGORY, subCategoryRecords);
    }

    async addGameMetaData(gameMetaData: [string, GameMetadataTable][], lobbyType: string, dataIdentifierKey: string, collectionKey: string) {
        const gameRecords = gameMetaData.map(([_, metadata]) => ({
            ...metadata,
            dataIdentifierKey,
            lobbyType,
            collectionKey,
        }));
        await this.casinoIndexDBService.addRecords<GameMetadataTable>(TABLES.GAME_METADATA, gameRecords);
    }

    async addStickerDetails(stickerMap: any[], lobbyType: string, dataIdentifierKey: string, collectionKey: string) {
        const stickerRecords: StickerDetailsTable[] = stickerMap.map((sticker) => ({
            name: sticker[0],
            value: sticker[1],
            lobbyType: lobbyType,
            dataIdentifierKey: dataIdentifierKey,
            collectionKey: collectionKey,
        }));
        await this.casinoIndexDBService.addRecords<StickerDetailsTable>(TABLES.STICKER_DETAILS, stickerRecords);
    }

    async addcategoryVsFeatureMetadata(
        categoryVsFeatureMetadata: CategoryFeatureMetadata[],
        lobbyType: string,
        dataIdentifierKey: string,
        collectionKey: string,
    ) {
        const categoryVsFeatureMetadataRecords = categoryVsFeatureMetadata?.map((categoryVsFeatureMetadataItem) => ({
            ...categoryVsFeatureMetadataItem,
            lobbyType: lobbyType,
            dataIdentifierKey: dataIdentifierKey,
            collectionKey: collectionKey,
        }));
        await this.casinoIndexDBService.addRecords<any>(TABLES.CATEGORY_VS_FEATURE_METADATA, categoryVsFeatureMetadataRecords);
    }

    async addLobbies(
        lobbyType: string,
        invokerProduct: string,
        templateName: string | undefined,
        lobbyGameIconImageSuffix: string,
        optSegName: string | undefined,
        dataIdentifierKey: string,
        collectionKey: string,
        lobbyTargetType: string,
    ) {
        const lobby: LobbyTable = {
            lobbyType: lobbyType,
            templateName: templateName,
            invokerProduct: invokerProduct,
            createdAt: Date.now(),
            lobbyGameIconImageSuffix: lobbyGameIconImageSuffix,
            optSegName: optSegName,
            dataIdentifierKey: dataIdentifierKey,
            collectionKey: collectionKey,
            lobbyTargetType: lobbyTargetType,
        };
        await this.casinoIndexDBService.addRecord<LobbyTable>(TABLES.LOBBY, lobby);
    }

    async addCategoryNameLangMap(categoryNameLangMap: [string, string][], lobbyType: string, dataIdentifierKey: string, collectionKey: string) {
        const categoryNameLangMapRecords: categoryNameLangTable[] = categoryNameLangMap.map((record) => ({
            name: record[0],
            value: record[1],
            lobbyType: lobbyType,
            dataIdentifierKey: dataIdentifierKey,
            collectionKey: collectionKey,
        }));
        await this.casinoIndexDBService.addRecords<categoryNameLangTable>(TABLES.CATEGORY_NAME_LANG_MAP, categoryNameLangMapRecords);
    }
}
